import { useEffect, useRef, useState } from 'react'
import type { CreationTask } from '../../api/creations'
import { retryAgentGeneration } from '../../api/agents'
import { subscribeCreationTask } from '../../lib/creationTaskLifecycle'
import { agentToolLabel } from './agentDisplay'
import { AgentImageStrip } from './AgentImageStrip'

/** 生图任务卡片数据（timeline.generate 的工具结果 / 失败事件归一而来） */
export type AgentGenerationCardData = {
  taskId: string
  status: 'running' | 'completed' | 'failed'
  model?: string
  modelLabel?: string
  aspect?: string
  n?: number
  platform?: string
  prompt?: string
  /** 生成中计时起点（毫秒）：运行时每秒跳动「已等待 n 秒」，完成时换算总用时 */
  startedAt?: number
  /** 终态烙存的总用时（秒） */
  elapsedSeconds?: number
  images?: string[]
  errorMessage?: string
}

type AgentGenerationCardProps = {
  blockId: string
  data: AgentGenerationCardData
  angular: boolean
  /** 状态变更回调：把 SSE 终态 / 重试后的新任务号同步回对话时间线块 */
  onChange?: (patch: Partial<AgentGenerationCardData>) => void
}

/** "1:1" / "1024x1024" → CSS aspect-ratio 值；无法识别返回 undefined */
function aspectCssValue(aspect?: string): string | undefined {
  const raw = (aspect ?? '').trim()
  if (!raw) return undefined
  const ratio = /^(\d+(?:\.\d+)?)\s*[:：]\s*(\d+(?:\.\d+)?)$/.exec(raw)
  if (ratio) return `${ratio[1]} / ${ratio[2]}`
  const size = /^(\d{2,5})\s*[x×]\s*(\d{2,5})$/i.exec(raw)
  if (size) return `${size[1]} / ${size[2]}`
  return undefined
}

/**
 * 智能体对话里的时间线生图卡片：
 * - running：按画幅比例的骨架占位 + 基本信息（模型 / 画幅 / 张数 / 平台）；
 * - completed：结果图 + 基本信息（图片条可放大）；
 * - failed：平台与错误信息 + 「重新生成」按钮（复用原任务参数重新入队，卡片原地更新）。
 * 任务终态经后端观察者链（创作任务状态机 → SSE task_updated）推回，断线由 hub 轮询兜底。
 */
export function AgentGenerationCard({ data, angular, onChange }: AgentGenerationCardProps) {
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState('')
  // onChange 可能随父组件重渲染换新引用，用 ref 避免订阅被反复重建
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const terminalHandledRef = useRef(false)

  // 计时起点：优先用入队事件烙下的 startedAt；重试换任务号时重置
  const startedAtRef = useRef<number>(data.startedAt ?? Date.now())
  useEffect(() => {
    startedAtRef.current = data.startedAt ?? Date.now()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.taskId])

  // 监听中计时：每秒跳动「已等待 n 秒」，让用户明确看到智能体仍在盯任务、没有断开
  const [waitingSeconds, setWaitingSeconds] = useState(0)
  useEffect(() => {
    if (data.status !== 'running') return undefined
    const tick = () => setWaitingSeconds(Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)))
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [data.status, data.taskId])

  useEffect(() => {
    if (data.status === 'completed' || data.status === 'failed') {
      terminalHandledRef.current = true
    }
    if (data.status !== 'running') return undefined
    const unsubscribe = subscribeCreationTask(data.taskId, (task: CreationTask) => {
      if (terminalHandledRef.current) return
      if (task.status === 'SUCCEEDED') {
        terminalHandledRef.current = true
        onChangeRef.current?.({
          status: 'completed',
          images: task.result?.imageUrls ?? [],
          platform: task.platform,
          errorMessage: undefined,
          elapsedSeconds: Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)),
        })
      } else if (task.status === 'FAILED') {
        terminalHandledRef.current = true
        onChangeRef.current?.({
          status: 'failed',
          platform: task.platform,
          errorMessage: task.errorMessage || '生成失败，原因未明',
          elapsedSeconds: Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)),
        })
      }
    })
    return unsubscribe
  }, [data.taskId, data.status])

  async function handleRetry() {
    if (retrying || !onChange) return
    setRetrying(true)
    setRetryError('')
    try {
      const next = await retryAgentGeneration(data.taskId)
      terminalHandledRef.current = false
      onChange({
        taskId: next.taskId,
        status: 'running',
        model: next.model ?? data.model,
        modelLabel: next.modelLabel ?? data.modelLabel,
        aspect: next.aspect ?? data.aspect,
        n: next.n ?? data.n,
        platform: next.platform ?? data.platform,
        prompt: data.prompt,
        startedAt: Date.now(),
        elapsedSeconds: undefined,
        images: undefined,
        errorMessage: undefined,
      })
    } catch (error) {
      setRetryError(error instanceof Error ? error.message : '重新生成失败，请稍后重试')
    } finally {
      setRetrying(false)
    }
  }

  const aspectStyle = aspectCssValue(data.aspect)
  // 骨架限高：以 360px 为高度上限，各画幅等比缩小宽度后水平居中（竖图不再撑满整屏）
  const skeletonRatio = aspectStyle ?? '1 / 1'
  const [ratioW, ratioH] = skeletonRatio.split('/').map((part) => Number.parseFloat(part.trim()))
  const skeletonWidth =
    Number.isFinite(ratioW) && Number.isFinite(ratioH) && ratioW > 0 && ratioH > 0
      ? `min(100%, calc(360px * ${ratioW} / ${ratioH}))`
      : '100%'
  const infoChips: string[] = []
  if (data.modelLabel || data.model) infoChips.push(data.modelLabel || data.model!)
  if (data.aspect) infoChips.push(data.aspect)
  if (data.n && data.n > 1) infoChips.push(`${data.n} 张`)
  if (data.platform) infoChips.push(`平台 ${data.platform}`)
  if (data.status !== 'running' && data.elapsedSeconds) infoChips.push(`用时 ${data.elapsedSeconds} 秒`)

  const cornerClass = angular ? 'rounded-none' : 'rounded-xl'
  const chipCorner = angular ? 'rounded-none' : 'rounded-md'

  return (
    <section
      className={[
        cornerClass,
        'w-full max-w-[26rem] border border-line bg-panel/70 p-3 shadow-card',
      ].join(' ')}
      aria-label={`生图任务 ${agentToolLabel('timeline.generate')}`}
    >
      <header className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-ink">时间线生图</span>
        <span
          className={[
            chipCorner,
            'border px-1.5 py-0.5 text-[10px] font-semibold',
            data.status === 'completed'
              ? 'border-[color-mix(in_srgb,var(--color-success)_45%,transparent)] bg-[color-mix(in_srgb,var(--color-success)_12%,transparent)] text-[color-mix(in_srgb,var(--color-success)_80%,var(--ink))]'
              : data.status === 'failed'
                ? 'border-[color-mix(in_srgb,var(--color-danger)_45%,transparent)] bg-[color-mix(in_srgb,var(--color-danger)_10%,transparent)] text-[color-mix(in_srgb,var(--color-danger)_85%,var(--ink))]'
                : 'border-line bg-surface-muted text-ink-soft',
          ].join(' ')}
        >
          {data.status === 'completed' ? '生成成功' : data.status === 'failed' ? '生成失败' : '生成中'}
        </span>
      </header>

      {data.status === 'completed' && data.images && data.images.length > 0 ? (
        <div className="mb-2">
          <AgentImageStrip
            toolName="display_images"
            images={data.images.map((src, index) => ({ src, alt: `生成结果 ${index + 1}` }))}
          />
        </div>
      ) : null}

      {data.status === 'running' ? (
        <div
          className={[
            cornerClass,
            'relative mx-auto mb-2 w-full overflow-hidden border border-line bg-surface-muted',
          ].join(' ')}
          style={{ aspectRatio: skeletonRatio, width: skeletonWidth }}
          aria-busy="true"
          aria-label="图片生成中"
        >
          <div className="absolute inset-0 animate-pulse bg-[linear-gradient(110deg,transparent,30%,color-mix(in_srgb,var(--panel)_85%,transparent),55%,transparent)]" />
          <span className="absolute left-2.5 top-2.5 rounded-md border border-line bg-panel px-2 py-1 text-[11px] font-semibold text-ink shadow-card">
            已等待 {waitingSeconds} 秒
          </span>
        </div>
      ) : null}

      {data.status === 'failed' ? (
        <p className="mb-2 break-words text-[12px] leading-relaxed text-[color-mix(in_srgb,var(--color-danger)_85%,var(--ink))]">
          {data.errorMessage || '生成失败，原因未明'}
        </p>
      ) : null}

      {infoChips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {infoChips.map((chip) => (
            <span
              key={chip}
              className={[
                chipCorner,
                'border border-line bg-surface-muted/70 px-1.5 py-0.5 font-mono text-[10px] text-ink-soft',
              ].join(' ')}
            >
              {chip}
            </span>
          ))}
        </div>
      ) : null}

      {data.status === 'failed' && onChange ? (
        <div className="mt-2.5">
          <button
            type="button"
            onClick={() => void handleRetry()}
            disabled={retrying}
            className={[
              chipCorner,
              'border border-accent/50 bg-accent/10 px-2.5 py-1.5 text-[12px] font-semibold text-accent transition-colors hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-60',
            ].join(' ')}
          >
            {retrying ? '正在重新生成…' : '重新生成'}
          </button>
          {retryError ? (
            <p className="mt-1.5 text-[11px] text-[color-mix(in_srgb,var(--color-danger)_85%,var(--ink))]">
              {retryError}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
